import { useState } from "react";
import { Paperclip } from "lucide-react";
import { useTranslation } from "@/shared/i18n/LocaleProvider";
import {
  attachmentErrorKey,
  fetchAttachmentContent,
  type ChatCallError,
} from "@/features/intelligence/api/client";
import { formatAttachmentSize } from "@/features/intelligence/lib/attachments";
import type { IntelligenceAttachmentRef } from "@/features/intelligence/api/shapes";
import { cn } from "@/shared/cn";
import { openBlobInline } from "@/shared/ui/blob";

export function MessageAttachments({
  attachments,
  conversationId,
  align,
}: {
  attachments: readonly IntelligenceAttachmentRef[];
  conversationId: string | null;
  align: "start" | "end";
}) {
  if (attachments.length === 0) return null;
  return (
    <ul
      className={cn(
        "flex flex-wrap gap-[var(--space-2)]",
        align === "end" ? "justify-end" : "justify-start",
      )}
    >
      {attachments.map((a) => (
        <li key={a.id}>
          <AttachmentLink attachment={a} conversationId={conversationId} />
        </li>
      ))}
    </ul>
  );
}

function AttachmentLink({
  attachment,
  conversationId,
}: {
  attachment: IntelligenceAttachmentRef;
  conversationId: string | null;
}) {
  const { t } = useTranslation();
  const [failure, setFailure] = useState<string | null>(null);
  const name = attachment.filename || t("intelligence.attachmentUnnamed");
  const size = formatAttachmentSize(attachment.size);
  const label = size ? `${name} · ${size}` : name;
  const shell = cn(
    "flex max-w-[280px] items-center gap-[var(--space-2)] rounded-[var(--radius-md)]",
    "border border-[rgba(251,251,245,0.5)] bg-[var(--color-light-03)]",
    "px-[var(--space-2)] py-[6px] font-(family-name:--font-display) text-xs",
    "text-[var(--color-text-strong)]",
  );
  const body = (
    <>
      <Paperclip size={13} className="shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{name}</span>
      {size !== "" && <span className="shrink-0 text-[var(--color-text-muted)]">{size}</span>}
    </>
  );

  if (!conversationId) {
    return (
      <div className={shell} title={label}>
        {body}
      </div>
    );
  }
  const open = async (): Promise<void> => {
    if (!conversationId) return;
    setFailure(null);
    try {
      await openBlobInline(async () => {
        const result = await fetchAttachmentContent(conversationId, attachment.id);
        if (!result.ok) throw new AttachmentOpenError(result.error);
        return result.value;
      });
    } catch (err) {
      setFailure(
        t(
          err instanceof AttachmentOpenError
            ? attachmentErrorKey(err.call)
            : "intelligence.attachmentOpenFailed",
        ),
      );
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => void open()}
        title={label}
        aria-label={t("intelligence.attachmentOpen", { name })}
        className={cn(
          shell,
          "cursor-pointer transition-colors duration-[var(--duration-fast)]",
          "hover:border-[var(--color-brand)] hover:bg-[var(--color-hover-faint)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
        )}
      >
        {body}
      </button>
      {failure && (
        <p
          role="status"
          className="mt-[4px] font-(family-name:--font-display) text-sm text-[var(--color-negative)]"
        >
          {failure}
        </p>
      )}
    </>
  );
}

class AttachmentOpenError extends Error {
  constructor(readonly call: ChatCallError) {
    super(`attachment content answered ${call.status}`);
  }
}
